import { useState, useEffect, useRef, useCallback } from "react";
import { Toast } from "primereact/toast";
import {
  getStationOwners,
  addStationOwner,
  deleteStationOwner,
  addStationOwnerPermission,
} from "../../../services/owners";
import type { StationOwner, StationRole } from "../../../types/owners";

const PERMISSION_ERROR_MESSAGES: Record<number, string> = {
  400: "Permission already exists.",
  401: "Unauthorized action.",
  403: "Access denied.",
};

export const useStationOwners = (stationId: string | undefined) => {
  const toast = useRef<Toast | null>(null);
  const [owners, setOwners] = useState<StationOwner[]>([]);
  const [email, setEmail] = useState("");
  const [selectedRole, setSelectedRole] = useState<StationRole>("owner");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const showToast = (
    severity: "success" | "info" | "warn" | "error",
    summary: string,
    detail: string,
  ) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const refreshOwners = useCallback(async () => {
    if (!stationId) return;
    const data = await getStationOwners(stationId);
    setOwners(data);
  }, [stationId]);

  useEffect(() => {
    if (!stationId) return;
    const load = async () => {
      setIsLoading(true);
      try {
        await refreshOwners();
        setError("");
      } catch {
        setError("Something went wrong, please try again later.");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [stationId, refreshOwners]);

  const addOwner = async () => {
    if (!stationId || !email) return;
    setIsLoading(true);
    try {
      await addStationOwner(stationId, email, selectedRole);
      await refreshOwners();

      showToast("success", "Added", `${email} added as ${selectedRole}`);
      setEmail("");
      setSelectedRole("owner");
    } catch (err: any) {
      const message = err?.response?.data?.message || "Failed to add";

      setError(message);
      showToast("error", "Error", message);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteOwner = async (ownerEmail: string) => {
    if (!stationId) return;
    setIsLoading(true);
    try {
      await deleteStationOwner(stationId, ownerEmail);
      await refreshOwners();
      showToast("info", "Deleted", `${ownerEmail} removed`);
    } catch (err: any) {
      const message = err?.response?.data?.message || "Failed to delete";
      showToast("error", "Error", message);
    } finally {
      setIsLoading(false);
    }
  };

  const initialisePermission = async () => {
    if (!stationId) return;
    setIsLoading(true);
    try {
      await addStationOwnerPermission(stationId);
      showToast(
        "success",
        "Permission Added",
        `Permission for ${stationId} granted`,
      );
    } catch (err: any) {
      const status = err?.response?.status;
      const message =
        PERMISSION_ERROR_MESSAGES[status] ?? "Unexpected error occurred.";
      showToast("warn", "Warning", message);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    toast,
    owners,
    email,
    setEmail,
    selectedRole,
    setSelectedRole,
    error,
    isLoading,
    addOwner,
    deleteOwner,
    initialisePermission,
  };
};
